import CopyableId from "@/components/common/CopyableId";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Activity, Video, Phone, FileText, Pill, ClipboardList,
  Stethoscope, MapPin, Clock, ShieldCheck, BookOpen, Users, Plus, HeartPulse,
  Sparkles, ShieldAlert, Gauge, FileClock, LogOut, Lock, AlarmClock, Bluetooth,
} from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FormField } from "@/components/common/FormSection";
import { MultiSelect } from "@/components/common/MultiSelect";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RISK_BADGE, PRIORITY_BADGE, timeAgo, VITAL_SHORT, READING_STATUS_BADGE } from "@/pages/clinical/constants";
import { OrdersTab, MarTab, NotesTab } from "@/pages/clinical/DocsTabs";
import ServiceOrdersTab from "@/pages/clinical/ServiceOrdersTab";
import { AllergiesTab, AllergiesBanner, ManualVitalsDialog } from "@/pages/clinical/AllergiesAndVitals";
import IotDeviceDialog from "@/components/clinical/IotVitalsCapture";
import PatientRecordingsTab from "@/pages/clinical/PatientRecordingsTab";
import PatientDocumentsTab from "@/pages/clinical/PatientDocumentsTab";
import { Ear, Headphones } from "lucide-react";
import CareTasksTab from "@/components/clinical/CareTasksTab";
import { ThresholdsTab } from "@/pages/clinical/ThresholdsTab";
import EmbeddedForms from "@/components/forms/EmbeddedForms";
import PatientEpisodes from "@/components/clinical/PatientEpisodes";
import CarePlanPatientTab from "@/components/clinical/CarePlanPatientTab";
import DispositionTab from "@/components/clinical/DispositionTab";

const TC_TYPES = {
  initial_assessment: "Initial Assessment", follow_up: "Follow-up",
  rpm_escalation: "RPM Escalation", hotline_escalation: "Hotline Escalation",
  physician_review: "Physician Review", nursing: "Nursing Consult",
  medication_review: "Medication Review", multidisciplinary: "Multidisciplinary",
};

const TIMELINE_COLORS = {
  onboarding: "#3B82F6", hotline: "#F59E0B", teleconsult: "#0B7E8C",
  encounter: "#475569", home_visit: "#10B981", rpm_alert: "#EF4444",
};

function ComingSoon({ title, description }) {
  return (
    <div className="hah-card p-8 text-center" data-testid="cl-coming-soon">
      <Sparkles className="w-8 h-8 mx-auto text-[#94A3B8] mb-2" />
      <h4 className="font-semibold text-[#1F2937]">{title} — Coming Soon</h4>
      <p className="text-sm text-[#6B7280] mt-1 max-w-md mx-auto">{description}</p>
      <span className="inline-block mt-3 px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded bg-[#FEF3C7] text-[#92400E]">
        Ships with Clinical Documentation & Orders module
      </span>
    </div>
  );
}

// ---------- Overview ----------
function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 864e5));
}

function OverviewTab({ summary }) {
  const p = summary?.patient || {};
  const rpm = summary?.rpm;
  const age = p.age ?? ageFromDob(p.dob);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div className="hah-card p-4 md:col-span-2">
        <div className="hah-section-title">Demographics</div>
        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
          <div><span className="text-[#6B7280]">MRN:</span> <strong className="font-mono"><CopyableId value={p.mrn} kind="MRN" /></strong></div>
          <div><span className="text-[#6B7280]">Full name:</span> {p.full_name || [p.first_name, p.second_name, p.middle_name, p.last_name].filter(Boolean).join(" ") || "—"}</div>
          <div><span className="text-[#6B7280]">DOB:</span> {p.dob || "—"} <span className="text-[10px] text-[#94A3B8]">({age ?? "—"}y)</span></div>
          <div><span className="text-[#6B7280]">Gender:</span> {p.gender || "—"}</div>
          <div><span className="text-[#6B7280]">Nationality:</span> {p.nationality || "—"}</div>
          <div><span className="text-[#6B7280]">Identity:</span> {p.identity_type ? `${p.identity_type} · ${p.identity_number || p.national_id || "—"}` : (p.national_id || "—")}</div>
          <div><span className="text-[#6B7280]">Phone:</span> {p.dial_code ? `${p.dial_code} ` : ""}{p.phone || "—"}</div>
          <div><span className="text-[#6B7280]">Email:</span> {p.email || "—"}</div>
          <div className="col-span-2"><span className="text-[#6B7280]">Address:</span> {typeof p.address === "string" ? p.address : (p.address?.line1 || p.address?.full || "—")}{p.zip_code ? ` · ${p.zip_code}` : ""}</div>
          <div><span className="text-[#6B7280]">Language:</span> {p.language === "ar" ? "Arabic" : p.language === "en" ? "English" : "—"}</div>
          <div><span className="text-[#6B7280]">Interpreter:</span> {p.requires_interpreter === true ? "Required" : p.requires_interpreter === false ? "Not required" : "—"}</div>
          {p.emergency_contact && (p.emergency_contact.first_name || p.emergency_contact.phone) && (
            <div className="col-span-2"><span className="text-[#6B7280]">Emergency contact:</span> {`${p.emergency_contact.first_name || ""} ${p.emergency_contact.last_name || ""}`.trim()} {p.emergency_contact.dial_code || ""} {p.emergency_contact.phone || ""}</div>
          )}
          {Array.isArray(p.special_needs) && p.special_needs.length > 0 && (
            <div className="col-span-2"><span className="text-[#6B7280]">Special needs:</span> {p.special_needs.join(", ")}</div>
          )}
          <div className="col-span-2"><span className="text-[#6B7280]">Primary diagnosis:</span> {p.primary_diagnosis || "—"}</div>
          <div className="col-span-2"><span className="text-[#6B7280]">Facility:</span> {summary?.facility?.name || "—"}</div>
        </div>
      </div>
      <div className="hah-card p-4">
        <div className="hah-section-title">Clinical Snapshot</div>
        <div className="mt-2 text-sm space-y-1.5">
          {rpm ? (
            <>
              <div className="flex items-center justify-between">
                <span className="text-[#6B7280]">NEWS2:</span>
                <strong className="text-[#1F2937]">{rpm.news2_score ?? "—"} <span className="text-xs text-[#6B7280] capitalize">({rpm.news2_risk})</span></strong>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#6B7280]">AI Risk:</span>
                <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold" style={{ background: (RISK_BADGE[rpm.ai_risk] || RISK_BADGE.null).bg, color: (RISK_BADGE[rpm.ai_risk] || RISK_BADGE.null).text }}>
                  {(RISK_BADGE[rpm.ai_risk] || RISK_BADGE.null).label}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[#6B7280]">Active alerts:</span>
                <strong className={rpm.active_alerts ? "text-[#EF4444]" : "text-[#1F2937]"}>{rpm.active_alerts}</strong>
              </div>
            </>
          ) : <div className="text-xs text-[#6B7280]">Not enrolled in RPM.</div>}
          <hr className="border-[#E5E7EB] my-2" />
          <div className="flex items-center justify-between">
            <span className="text-[#6B7280]">Upcoming Teleconsults:</span>
            <strong>{summary?.upcoming_teleconsult_count ?? 0}</strong>
          </div>
          {summary?.last_hotline && (
            <div className="text-xs text-[#6B7280] pt-1">
              Last hotline: <strong className="text-[#1F2937]">{summary.last_hotline.record_id}</strong> · {timeAgo(summary.last_hotline.created_at)}
            </div>
          )}
          {summary?.upcoming_visit && (
            <div className="text-xs text-[#6B7280]">
              Next home visit: <strong className="text-[#1F2937]">{summary.upcoming_visit.visit_type}</strong> · {new Date(summary.upcoming_visit.scheduled_at).toLocaleString()}
            </div>
          )}
          {summary?.last_encounter && (
            <div className="text-xs text-[#6B7280]">
              Last encounter: {timeAgo(summary.last_encounter.ended_at)} · <span className="capitalize">{summary.last_encounter.consultation_type}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Care Team ----------
const WRITE_CARE_TEAM_ROLES = new Set([
  "admin", "clinical_director", "hah_coordinator", "care_coordinator",
]);

function CareTeamTab({ patientId }) {
  const [data, setData] = useState({ items: [], assigned_teams: [] });

  const load = useCallback(() => {
    api.get(`/clinical-ops/patients/${patientId}/care-team`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const members = data.items || [];

  return (
    <div className="space-y-4" data-testid="cl-care-team">
      {/* ----- Full Care Roster (aggregated, driven by the physician's Care Plan + onboarding) ----- */}
      <div className="hah-card p-5">
        <h3 className="text-base font-semibold text-[#1F2937] mb-1">Care Roster</h3>
        <p className="text-xs text-[#6B7280] mb-3">
          The responsible care team is set by the physician's Care Plan and the onboarding assignment.
        </p>
        {members.length === 0 ? (
          <div className="text-sm text-[#6B7280]">No care team members assigned yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {members.map((m) => (
              <div key={`${m.user_id}-${m.team_role}`} className="border border-[#E5E7EB] rounded-lg p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center font-bold">
                  {(m.name || "?").charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#1F2937]">{m.name}</div>
                  <div className="text-xs text-[#6B7280]">{m.team_role} · <span className="capitalize">{(m.role_code || "").replace(/_/g, " ")}</span></div>
                  <div className="text-xs text-[#94A3B8]">{m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- RPM ----------
function RpmTab({ patientId, summary }) {
  const navigate = useNavigate();
  const rpm = summary?.rpm;
  return (
    <div className="space-y-3" data-testid="cl-rpm-tab">
      <div className="hah-card p-5 flex items-center justify-between">
        <div>
          <div className="hah-section-title">Remote Patient Monitoring</div>
          <div className="text-sm text-[#6B7280] mt-1">
            {rpm ? <>NEWS2 <strong>{rpm.news2_score ?? "—"}</strong> · AI risk <strong className="capitalize">{rpm.ai_risk || "—"}</strong> · <strong className={rpm.active_alerts ? "text-[#EF4444]" : ""}>{rpm.active_alerts} open alerts</strong></> : "Not enrolled."}
          </div>
        </div>
        {rpm && (
          <Button onClick={() => navigate(`/rpm/patients/${patientId}`)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cl-rpm-open">
            <Activity className="w-4 h-4 mr-1.5" /> Open RPM Detail
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------- Teleconsultations ----------
function TeleconsultTab({ patientId, readOnly }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [openSched, setOpenSched] = useState(false);
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({
    consultation_type: "follow_up", provider_ids: [], scheduled_at: "",
    duration_minutes: 30, priority: "routine", notes: "", source_module: "manual",
  });

  const load = useCallback(() => {
    Promise.all([
      api.get("/teleconsult/consultations", { params: { patient_id: patientId, page_size: 50 } }),
      api.get("/teleconsult/encounters", { params: { patient_id: patientId } }),
    ]).then(([c, e]) => { setItems(c.data.items || []); setEncounters(e.data.items || []); }).catch(() => {});
  }, [patientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!openSched) return;
    api.get("/users", { params: { page_size: 100 } })
      .then((r) => setProviders((r.data.items || []).filter((u) => ["physician", "hah_physician", "hah_nurse", "nurse"].includes(u.role_code))))
      .catch(() => {});
  }, [openSched]);

  async function schedule() {
    try {
      const [primary_provider_id, ...participants] = form.provider_ids;
      const r = await api.post("/teleconsult/consultations", {
        patient_id: patientId,
        ...form,
        primary_provider_id: primary_provider_id || null,
        participants,
        scheduled_at: form.scheduled_at || null,
        provider_ids: undefined,
      });
      toast.success("Teleconsultation scheduled");
      setOpenSched(false);
      navigate(`/teleconsult/room/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function startNow() {
    try {
      const r = await api.post("/teleconsult/consultations", {
        patient_id: patientId, consultation_type: "physician_review",
        priority: "routine", duration_minutes: 30, source_module: "manual",
      });
      toast.success("Teleconsultation created");
      navigate(`/teleconsult/room/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const upcoming = items.filter((c) => ["scheduled", "awaiting_participants", "in_progress"].includes(c.status));
  const completed = items.filter((c) => c.status === "completed");

  return (
    <div className="space-y-4" data-testid="cl-tele-tab">
      <div className="hah-card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[#1F2937]">Teleconsultations</h3>
          {!readOnly && (
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={startNow} className="border-[#E5E7EB]" data-testid="cl-tele-start-now">
                <Video className="w-3.5 h-3.5 mr-1.5" /> Start Now
              </Button>
              <Button size="sm" onClick={() => setOpenSched(true)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cl-tele-schedule">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Schedule
              </Button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="hah-section-title mb-2">Upcoming</div>
            {upcoming.length === 0 ? <div className="text-sm text-[#6B7280]">None scheduled.</div> : upcoming.map((c) => (
              <button key={c.id} onClick={() => navigate(`/teleconsult/room/${c.id}`)} className="w-full text-left p-3 mb-2 border border-[#E5E7EB] rounded-lg hover:bg-[#EAF8FA]/40">
                <div className="text-sm font-medium text-[#1F2937]">{TC_TYPES[c.consultation_type] || c.consultation_type}</div>
                <div className="text-xs text-[#6B7280]">{c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "Unscheduled"} · <span className="capitalize">{c.status.replace("_", " ")}</span></div>
              </button>
            ))}
          </div>
          <div>
            <div className="hah-section-title mb-2">Completed</div>
            {completed.length === 0 ? <div className="text-sm text-[#6B7280]">No completed sessions.</div> : completed.slice(0, 5).map((c) => (
              <button key={c.id} onClick={() => navigate(`/teleconsult/room/${c.id}`)} className="w-full text-left p-3 mb-2 border border-[#E5E7EB] rounded-lg hover:bg-[#FAFBFC]">
                <div className="text-sm font-medium text-[#1F2937]">{TC_TYPES[c.consultation_type] || c.consultation_type}</div>
                <div className="text-xs text-[#6B7280]">{c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : "—"}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="hah-card p-5">
        <h4 className="font-semibold text-[#1F2937] mb-2">Encounter Summaries</h4>
        {encounters.length === 0 ? <div className="text-sm text-[#6B7280]">No encounters recorded.</div> : (
          <ul className="space-y-3 text-sm">
            {encounters.slice(0, 8).map((e) => (
              <li key={e.id} className="border-b border-[#E5E7EB] pb-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[#1F2937] capitalize">{(e.consultation_type || "").replace(/_/g, " ")}</span>
                  <span className="text-xs text-[#6B7280]">{e.ended_at ? new Date(e.ended_at).toLocaleString() : "—"} · {e.duration_minutes ?? 0}min</span>
                </div>
                {e.assessment && <div className="text-xs text-[#6B7280] mt-1">{e.assessment}</div>}
                {e.diagnosis && <div className="text-xs text-[#0B7E8C] mt-0.5">Dx: {e.diagnosis}</div>}
                {e.recommendations && <div className="text-xs text-[#6B7280] mt-0.5">Recs: {e.recommendations}</div>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={openSched} onOpenChange={setOpenSched}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Schedule Teleconsultation</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <FormField label="Consultation Type">
              <Select value={form.consultation_type} onValueChange={(v) => setForm({ ...form, consultation_type: v })}>
                <SelectTrigger data-testid="cl-tele-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TC_TYPES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </FormField>
            <FormField label="Source">
              <Select value={form.source_module} onValueChange={(v) => setForm({ ...form, source_module: v })}>
                <SelectTrigger data-testid="cl-tele-form-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rpm_alert">RPM Alert</SelectItem>
                  <SelectItem value="hotline">Nurse Hotline</SelectItem>
                  <SelectItem value="home_visit">Home Visit</SelectItem>
                  <SelectItem value="follow_up_review">Follow-Up Review</SelectItem>
                  <SelectItem value="physician_request">Physician Request</SelectItem>
                  <SelectItem value="care_coordinator_request">Care Coordinator Request</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Providers">
              <MultiSelect
                testid="cl-tele-form-providers"
                placeholder="Optional — pick one or more"
                primaryLabel="Primary"
                emptyText="No providers found"
                values={form.provider_ids}
                onChange={(ids) => setForm({ ...form, provider_ids: ids })}
                options={providers.map((u) => ({
                  value: u.id,
                  label: `${u.first_name} ${u.last_name}`,
                  sublabel: u.role_code,
                }))}
              />
            </FormField>
            <FormField label="Date & Time">
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="cl-tele-form-when" />
            </FormField>
            <FormField label="Duration (min)">
              <Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 30 })} />
            </FormField>
            <FormField label="Priority">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Notes" full>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </FormField>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenSched(false)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={schedule} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cl-tele-form-save">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Hotline ----------
function HotlineTab({ patientId, readOnly }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState({ sessions: [], escalations: [], follow_ups: [] });
  const [openFU, setOpenFU] = useState(null);    // session for follow-up dialog
  const [openEsc, setOpenEsc] = useState(null);  // session for escalation dialog
  const [openNote, setOpenNote] = useState(null);// session for progress-note dialog

  const role = user?.role_code;
  const canFollowUp = !readOnly && ["admin", "hah_nurse", "nurse", "hah_coordinator", "care_coordinator", "hah_physician", "physician"].includes(role);
  const canEscalate = !readOnly && ["admin", "hah_nurse", "nurse", "hah_coordinator", "care_coordinator"].includes(role);
  const noteCategory = readOnly ? null : (
    ["physician", "hah_physician", "clinical_director"].includes(role) ? "physician" :
    ["nurse", "hah_nurse"].includes(role) ? "nursing" :
    ["care_coordinator", "hah_coordinator"].includes(role) ? "coordination" : null
  );

  const load = useCallback(() => {
    api.get(`/hotline/patients/${patientId}/sessions`).then((r) => setData(r.data)).catch(() => {});
  }, [patientId]);
  useEffect(() => { load(); }, [load]);

  async function createTeleFromSession(s) {
    try {
      const r = await api.post("/teleconsult/consultations", {
        patient_id: patientId, consultation_type: "hotline_escalation",
        priority: "urgent", duration_minutes: 30,
        source_module: "hotline", source_id: s.id,
        notes: `Initiated from hotline session ${s.record_id}.`,
      });
      toast.success("Teleconsultation created");
      navigate(`/teleconsult/room/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-4" data-testid="cl-hotline-tab">
      <div className="hah-card p-5">
        <h3 className="text-base font-semibold text-[#1F2937] mb-3">Hotline Sessions</h3>
        {data.sessions.length === 0 ? <div className="text-sm text-[#6B7280]">No hotline sessions.</div> : (
          <div className="space-y-2">
            {data.sessions.map((s) => (
              <div key={s.id} className="p-3 border border-[#E5E7EB] rounded-lg" data-testid={`cl-hotline-row-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <button onClick={() => navigate(`/hotline/sessions/${s.id}`)} className="text-left flex-1 min-w-0 hover:text-[#0B7E8C]">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-[#0B7E8C] font-medium">{s.record_id}</span>
                      <span className="text-xs text-[#6B7280] capitalize">· {(s.status || "").replace(/_/g, " ")}</span>
                    </div>
                    <div className="text-sm text-[#1F2937] mt-0.5">{s.chief_complaint?.description || "—"}</div>
                    <div className="text-xs text-[#6B7280] mt-0.5">{new Date(s.created_at).toLocaleString()}</div>
                  </button>
                </div>
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/hotline/sessions/${s.id}`)}
                    className="h-7 text-xs border-[#E5E7EB]" data-testid={`cl-hotline-view-${s.id}`}>
                    View Session
                  </Button>
                  {!readOnly && (
                    <Button size="sm" variant="outline" onClick={() => createTeleFromSession(s)}
                      className="h-7 text-xs border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]" data-testid={`cl-hotline-tele-${s.id}`}>
                      <Video className="w-3 h-3 mr-1" /> Create Teleconsult
                    </Button>
                  )}
                  {canFollowUp && (
                    <Button size="sm" variant="outline" onClick={() => setOpenFU(s)}
                      className="h-7 text-xs border-[#E5E7EB]" data-testid={`cl-hotline-followup-${s.id}`}>
                      <Clock className="w-3 h-3 mr-1" /> Create Follow-Up
                    </Button>
                  )}
                  {canEscalate && (
                    <Button size="sm" variant="outline" onClick={() => setOpenEsc(s)}
                      className="h-7 text-xs border-[#EF4444] text-[#EF4444] hover:bg-[#FEE2E2]/40" data-testid={`cl-hotline-escalate-${s.id}`}>
                      <Stethoscope className="w-3 h-3 mr-1" /> Escalate to Physician
                    </Button>
                  )}
                  {noteCategory && (
                    <Button size="sm" variant="outline" onClick={() => setOpenNote(s)}
                      className="h-7 text-xs border-[#E5E7EB]" data-testid={`cl-hotline-note-${s.id}`}>
                      <FileText className="w-3 h-3 mr-1" /> Create Progress Note
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="hah-card p-5">
          <h4 className="font-semibold text-[#1F2937] mb-2">Escalations</h4>
          {data.escalations.length === 0 ? <div className="text-sm text-[#6B7280]">None.</div> : (
            <ul className="space-y-2 text-sm">
              {data.escalations.map((e) => (
                <li key={e.id} className="border-b border-[#E5E7EB] pb-1.5">
                  <div className="font-medium text-[#1F2937] capitalize">{(e.type || "").replace(/_/g, " ")}</div>
                  <div className="text-xs text-[#6B7280]">{e.reason} · <span className="capitalize">{(e.status || "").replace(/_/g, " ")}</span></div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="hah-card p-5">
          <h4 className="font-semibold text-[#1F2937] mb-2">Follow-Ups</h4>
          {data.follow_ups.length === 0 ? <div className="text-sm text-[#6B7280]">None.</div> : (
            <ul className="space-y-2 text-sm">
              {data.follow_ups.map((f) => (
                <li key={f.id} className="border-b border-[#E5E7EB] pb-1.5">
                  <div className="font-medium text-[#1F2937]">Due {f.due_date}</div>
                  <div className="text-xs text-[#6B7280]">{f.reason} · <span className="capitalize">{(f.status || "").replace(/_/g, " ")}</span></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <HotlineFollowUpDialog open={!!openFU} session={openFU} onClose={() => setOpenFU(null)} onCreated={load} />
      <HotlineEscalateDialog open={!!openEsc} session={openEsc} onClose={() => setOpenEsc(null)} onCreated={load} />
      <HotlineProgressNoteDialog open={!!openNote} session={openNote} category={noteCategory} onClose={() => setOpenNote(null)} />
    </div>
  );
}

function HotlineFollowUpDialog({ open, session, onClose, onCreated }) {
  const [form, setForm] = useState({ assignee_id: "", due_date: "", priority: "routine", reason: "", notes: "" });
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!open) return;
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    setForm({ assignee_id: "", due_date: tomorrow.toISOString().slice(0, 10),
              priority: "routine", reason: session?.chief_complaint?.description || "Follow-up after hotline session", notes: "" });
    api.get("/users", { params: { page_size: 100 } })
      .then((r) => setUsers((r.data.items || []).filter((u) => ["hah_nurse", "nurse", "hah_coordinator", "care_coordinator", "hah_physician", "physician"].includes(u.role_code))))
      .catch(() => {});
  }, [open, session]);

  async function submit() {
    if (!form.assignee_id || !form.due_date || !form.reason) return toast.error("Assignee, due date and reason are required");
    try {
      await api.post(`/hotline/sessions/${session.id}/follow-ups`, form);
      toast.success("Follow-up scheduled");
      onCreated(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Create Follow-Up</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <FormField label="Assignee">
            <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
              <SelectTrigger data-testid="cl-fu-assignee"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name} · {u.role_code}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Due Date">
            <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="cl-fu-date" />
          </FormField>
          <FormField label="Priority">
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="routine">Routine</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Reason" full>
            <Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cl-fu-reason" />
          </FormField>
          <FormField label="Notes" full>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={submit} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cl-fu-save">Schedule Follow-Up</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HotlineEscalateDialog({ open, session, onClose, onCreated }) {
  const [form, setForm] = useState({ type: "physician", reason: "", priority: "high", comments: "", assignee_id: "" });
  const [physicians, setPhysicians] = useState([]);
  useEffect(() => {
    if (!open) return;
    setForm({ type: "physician", reason: session?.chief_complaint?.description || "", priority: "high", comments: "", assignee_id: "" });
    api.get("/users", { params: { page_size: 100 } })
      .then((r) => setPhysicians((r.data.items || []).filter((u) => ["physician", "hah_physician", "clinical_director"].includes(u.role_code))))
      .catch(() => {});
  }, [open, session]);

  async function submit() {
    if (!form.reason.trim()) return toast.error("Reason required");
    try {
      await api.post(`/hotline/sessions/${session.id}/escalations`, form);
      toast.success("Escalated to physician");
      onCreated(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Escalate to Physician</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <FormField label="Assign to Physician (optional)">
            <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
              <SelectTrigger data-testid="cl-esc-assignee"><SelectValue placeholder="Auto-assign on-call" /></SelectTrigger>
              <SelectContent>{physicians.map((u) => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Priority">
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="routine">Routine</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Reason" full>
            <Textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cl-esc-reason" />
          </FormField>
          <FormField label="Comments" full>
            <Textarea rows={2} value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={submit} className="bg-[#EF4444] hover:bg-[#DC2626] text-white" data-testid="cl-esc-save">Escalate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HotlineProgressNoteDialog({ open, session, category, onClose }) {
  const [form, setForm] = useState({ subjective: "", objective: "", assessment: "", plan: "", narrative: "" });
  useEffect(() => {
    if (!open || !session) return;
    const ref = `[Re: hotline session ${session.record_id}] ${session.chief_complaint?.description || ""}`;
    setForm({
      subjective: category === "physician" ? ref : "",
      objective: "", assessment: "", plan: "",
      narrative: category !== "physician" ? ref : "",
    });
  }, [open, session, category]);

  async function submit() {
    try {
      const payload = { patient_id: session.patient_id, category };
      if (category === "physician") {
        Object.assign(payload, { subjective: form.subjective, objective: form.objective, assessment: form.assessment, plan: form.plan });
      } else {
        payload.narrative = form.narrative;
      }
      await api.post("/clinical-docs/notes", payload);
      toast.success("Progress note saved as draft");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Create {category && (category[0].toUpperCase() + category.slice(1))} Note</DialogTitle></DialogHeader>
        {category && (
          <div className="space-y-3 py-2">
            {category === "physician" ? (
              <>
                <FormField label="Subjective" full><Textarea rows={2} value={form.subjective} onChange={(e) => setForm({ ...form, subjective: e.target.value })} data-testid="cl-note-s" /></FormField>
                <FormField label="Objective" full><Textarea rows={2} value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} /></FormField>
                <FormField label="Assessment" full><Textarea rows={2} value={form.assessment} onChange={(e) => setForm({ ...form, assessment: e.target.value })} /></FormField>
                <FormField label="Plan" full><Textarea rows={2} value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} /></FormField>
              </>
            ) : (
              <FormField label="Narrative" full><Textarea rows={6} value={form.narrative} onChange={(e) => setForm({ ...form, narrative: e.target.value })} data-testid="cl-note-narrative" /></FormField>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB]">Cancel</Button>
          <Button onClick={submit} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="cl-hotline-note-save">Save Note</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Encounters ----------
function EncountersTab({ patientId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/teleconsult/encounters", { params: { patient_id: patientId } }).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [patientId]);
  return (
    <div className="hah-card p-5" data-testid="cl-encounters-tab">
      <h3 className="text-base font-semibold text-[#1F2937] mb-3">Encounters</h3>
      {items.length === 0 ? <div className="text-sm text-[#6B7280]">No encounters recorded yet.</div> : (
        <ul className="space-y-3 text-sm">
          {items.map((e) => (
            <li key={e.id} className="border border-[#E5E7EB] rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="font-medium text-[#1F2937] capitalize">{(e.consultation_type || "").replace(/_/g, " ")}</span>
                <span className="text-xs text-[#6B7280]">{e.ended_at ? new Date(e.ended_at).toLocaleString() : "—"} · {e.duration_minutes ?? 0}min</span>
              </div>
              {e.diagnosis && <div className="text-xs text-[#0B7E8C] mt-1">Dx: {e.diagnosis}</div>}
              {e.assessment && <div className="text-xs text-[#6B7280] mt-1"><strong>Assessment:</strong> {e.assessment}</div>}
              {e.recommendations && <div className="text-xs text-[#6B7280] mt-1"><strong>Recommendations:</strong> {e.recommendations}</div>}
              {e.follow_up_required && e.follow_up_notes && (
                <div className="text-xs text-[#92400E] mt-1 bg-[#FEF3C7] p-2 rounded"><strong>Follow-up:</strong> {e.follow_up_notes}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Home Visits ----------
function HomeVisitsTab({ patientId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/fleet/visits", { params: { patient_id: patientId, page_size: 50 } }).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [patientId]);
  return (
    <div className="hah-card p-5" data-testid="cl-visits-tab">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-[#1F2937]">Home Visits</h3>
        <Button size="sm" variant="outline" onClick={() => navigate("/fleet/visits")} className="border-[#E5E7EB]">
          <MapPin className="w-3.5 h-3.5 mr-1.5" /> Visit Board
        </Button>
      </div>
      {items.length === 0 ? <div className="text-sm text-[#6B7280]">No home visits scheduled.</div> : (
        <ul className="space-y-2 text-sm">
          {items.map((v) => (
            <li key={v.id} className="p-3 border border-[#E5E7EB] rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-[#1F2937] capitalize">{(v.visit_type || "").replace(/_/g, " ")}</span>
                <span className="text-xs text-[#6B7280] capitalize">{(v.status || "").replace(/_/g, " ")}</span>
              </div>
              <div className="text-xs text-[#6B7280] mt-0.5">{v.scheduled_at ? new Date(v.scheduled_at).toLocaleString() : "—"} · {v.clinician_name || "Unassigned"}</div>
              {v.notes && <div className="text-xs text-[#6B7280] mt-1">{v.notes}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Timeline ----------
function TimelineTab({ patientId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/clinical-ops/patients/${patientId}/timeline`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [patientId]);
  return (
    <div className="hah-card p-5" data-testid="cl-timeline-tab">
      <h3 className="text-base font-semibold text-[#1F2937] mb-4">Clinical Timeline</h3>
      {items.length === 0 ? <div className="text-sm text-[#6B7280]">No events.</div> : (
        <ol className="relative border-l-2 border-[#E5E7EB] ml-3 space-y-5">
          {items.map((ev, i) => (
            <li key={i} className="ml-4">
              <span className="absolute -left-[7px] w-3 h-3 rounded-full ring-4 ring-white" style={{ background: TIMELINE_COLORS[ev.category] || "#94A3B8" }} />
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ background: `${TIMELINE_COLORS[ev.category]}15`, color: TIMELINE_COLORS[ev.category] }}>{(ev.category || "").replace(/_/g, " ")}</span>
                <span className="text-xs text-[#6B7280]">{ev.at ? new Date(ev.at).toLocaleString() : ""}</span>
              </div>
              <div className="text-sm font-medium text-[#1F2937] mt-1">{ev.title}</div>
              {ev.description && <div className="text-xs text-[#6B7280] mt-0.5">{ev.description}</div>}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------- Audit ----------
function AuditTab({ patientId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/clinical-ops/patients/${patientId}/audit`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, [patientId]);
  return (
    <div className="hah-card p-5" data-testid="cl-audit-tab">
      <h3 className="text-base font-semibold text-[#1F2937] mb-3">Audit History</h3>
      {items.length === 0 ? <div className="text-sm text-[#6B7280]">No audit entries.</div> : (
        <ul className="text-sm divide-y divide-[#E5E7EB]">
          {items.map((a) => (
            <li key={a.id} className="py-2 flex items-center justify-between">
              <div>
                <span className="font-mono text-xs text-[#0B7E8C]">{a.action}</span>
                <span className="text-xs text-[#6B7280] ml-2">{a.entity_type}</span>
              </div>
              <div className="text-xs text-[#6B7280]">{a.user_email} · {new Date(a.created_at).toLocaleString()}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- MAIN ----------
export default function ClinicalPatientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const canCareTasks = ["hah_nurse", "nurse", "hah_coordinator", "care_coordinator", "admin"].includes(user?.role_code);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const [allergies, setAllergies] = useState([]);
  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [iotOpen, setIotOpen] = useState(false);
  const [placedForms, setPlacedForms] = useState([]);

  useEffect(() => {
    api.get("/forms/embedded", { params: { module: "patient", entity_id: id } })
      .then((r) => setPlacedForms(r.data.items)).catch(() => {});
  }, [id]);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/clinical-ops/patients/${id}/summary`);
      setSummary(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const loadAllergies = useCallback(async () => {
    try {
      const r = await api.get(`/patients/${id}/allergies`);
      setAllergies(r.data.items || []);
    } catch (e) { /* silent — banner just falls back to NKA */ }
  }, [id]);
  useEffect(() => { loadAllergies(); }, [loadAllergies]);

  if (!summary) return <div className="p-6 text-sm text-[#6B7280]">Loading…</div>;
  const p = summary.patient;
  const role = user?.role_code;
  const canOrder = ["admin", "physician", "hah_physician"].includes(role);
  const canMar = ["admin", "nurse", "hah_nurse"].includes(role);
  const canNotes = ["admin", "physician", "hah_physician", "nurse", "hah_nurse", "care_coordinator", "hah_coordinator"].includes(role);
  const canRecordVitals = ["admin", "physician", "hah_physician", "nurse", "hah_nurse", "clinical_director"].includes(role);
  // Once a patient is dispositioned the record becomes view-only — no clinical actions allowed.
  const isDispositioned = !!p.disposition || p.status === "dispositioned";

  return (
    <div data-testid="clinical-patient-profile">
      <PageHeader
        title={<span>{[p.first_name, p.second_name, p.middle_name, p.last_name].filter(Boolean).join(" ")} <span className="text-sm font-mono text-[#94A3B8]"><CopyableId value={p.mrn} kind="MRN" /></span>{p.disposition && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full align-middle" style={{ backgroundColor: "#6B72801A", color: "#475569" }} data-testid="cl-disposition-indicator">
            <Lock className="w-3 h-3" />{p.disposition.label}
          </span>
        )}</span>}
        subtitle={`${p.age ?? ageFromDob(p.dob) ?? "—"}y · ${p.gender || "—"} · ${p.primary_diagnosis || "—"}`}
        actions={
          <div className="flex items-center gap-2">
            {!isDispositioned && canRecordVitals && (
              <Button
                onClick={() => setVitalsOpen(true)}
                className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
                data-testid="cl-record-vitals"
              >
                <Activity className="w-4 h-4 mr-1.5" /> Record Vitals
              </Button>
            )}
            {!isDispositioned && canRecordVitals && (
              <Button
                onClick={() => setIotOpen(true)}
                variant="outline"
                className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#F0FAFB]"
                data-testid="cl-connect-device"
              >
                <Bluetooth className="w-4 h-4 mr-1.5" /> Connect Device
              </Button>
            )}
            {!isDispositioned && (
              <Button
                onClick={() => setTab("disposition")}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="cl-disposition-action"
              >
                <LogOut className="w-4 h-4 mr-1.5" /> Disposition
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate("/clinical")} className="border-[#E5E7EB]" data-testid="cl-profile-back">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Active Patients
            </Button>
          </div>
        }
      />

      {isDispositioned && (
        <div className="mb-3 p-3 rounded-lg bg-[#F1F5F9] border border-[#CBD5E1] text-sm text-[#475569] flex items-center gap-2" data-testid="cl-dispositioned-readonly-banner">
          <Lock className="w-4 h-4 shrink-0" />
          This patient has been dispositioned{p.disposition?.label ? ` (${p.disposition.label})` : ""}. The record is now view-only — no clinical actions can be performed.
        </div>
      )}

      <AllergiesBanner allergies={allergies} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap bg-transparent h-auto p-0 gap-1 mb-3">
          <TabsTrigger value="overview" className="hah-tab" data-testid="cl-tab-overview"><BookOpen className="w-3.5 h-3.5 mr-1" />Overview</TabsTrigger>
          <TabsTrigger value="care_team" className="hah-tab" data-testid="cl-tab-care-team"><Users className="w-3.5 h-3.5 mr-1" />Care Team</TabsTrigger>
          <TabsTrigger value="care_plan" className="hah-tab" data-testid="cl-tab-care-plan"><ClipboardList className="w-3.5 h-3.5 mr-1" />Care Plan</TabsTrigger>
          <TabsTrigger value="allergies" className="hah-tab" data-testid="cl-tab-allergies">
            <ShieldAlert className="w-3.5 h-3.5 mr-1" />
            Allergies
            {allergies.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold">
                {allergies.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="rpm" className="hah-tab" data-testid="cl-tab-rpm"><HeartPulse className="w-3.5 h-3.5 mr-1" />RPM</TabsTrigger>
          {canCareTasks && <TabsTrigger value="reminders" className="hah-tab" data-testid="cl-tab-reminders"><AlarmClock className="w-3.5 h-3.5 mr-1" />Reminders</TabsTrigger>}
          <TabsTrigger value="thresholds" className="hah-tab" data-testid="cl-tab-thresholds"><Gauge className="w-3.5 h-3.5 mr-1" />Thresholds</TabsTrigger>
          <TabsTrigger value="teleconsult" className="hah-tab" data-testid="cl-tab-teleconsult"><Video className="w-3.5 h-3.5 mr-1" />Teleconsultations</TabsTrigger>
          <TabsTrigger value="hotline" className="hah-tab" data-testid="cl-tab-hotline"><Phone className="w-3.5 h-3.5 mr-1" />Nurse Hotline</TabsTrigger>
          <TabsTrigger value="orders" className="hah-tab" data-testid="cl-tab-orders"><ClipboardList className="w-3.5 h-3.5 mr-1" />Orders</TabsTrigger>
          <TabsTrigger value="mar" className="hah-tab" data-testid="cl-tab-mar"><Pill className="w-3.5 h-3.5 mr-1" />MAR</TabsTrigger>
          <TabsTrigger value="notes" className="hah-tab" data-testid="cl-tab-notes"><FileText className="w-3.5 h-3.5 mr-1" />Progress Notes</TabsTrigger>
          <TabsTrigger value="encounters" className="hah-tab" data-testid="cl-tab-encounters"><Stethoscope className="w-3.5 h-3.5 mr-1" />Encounters</TabsTrigger>
          <TabsTrigger value="otoscope" className="hah-tab" data-testid="cl-tab-otoscope"><Ear className="w-3.5 h-3.5 mr-1" />Otoscope</TabsTrigger>
          <TabsTrigger value="stethoscope" className="hah-tab" data-testid="cl-tab-stethoscope"><Headphones className="w-3.5 h-3.5 mr-1" />Stethoscope</TabsTrigger>
          <TabsTrigger value="documents" className="hah-tab" data-testid="cl-tab-documents"><FileText className="w-3.5 h-3.5 mr-1" />Documents</TabsTrigger>
          <TabsTrigger value="visits" className="hah-tab" data-testid="cl-tab-visits"><MapPin className="w-3.5 h-3.5 mr-1" />Home Visits</TabsTrigger>
          <TabsTrigger value="services" className="hah-tab" data-testid="cl-tab-services"><ClipboardList className="w-3.5 h-3.5 mr-1" />Services</TabsTrigger>
          <TabsTrigger value="timeline" className="hah-tab" data-testid="cl-tab-timeline"><Clock className="w-3.5 h-3.5 mr-1" />Timeline</TabsTrigger>
          <TabsTrigger value="episodes" className="hah-tab" data-testid="cl-tab-episodes"><FileClock className="w-3.5 h-3.5 mr-1" />Episodes</TabsTrigger>
          <TabsTrigger value="audit" className="hah-tab" data-testid="cl-tab-audit"><ShieldCheck className="w-3.5 h-3.5 mr-1" />Audit</TabsTrigger>
          {placedForms.length > 0 && <TabsTrigger value="forms" className="hah-tab" data-testid="cl-tab-forms"><FileText className="w-3.5 h-3.5 mr-1" />Forms</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview"><OverviewTab summary={summary} /></TabsContent>
        <TabsContent value="care_team"><CareTeamTab patientId={id} /></TabsContent>
        <TabsContent value="care_plan"><CarePlanPatientTab patientId={id} readOnly={isDispositioned} /></TabsContent>
        <TabsContent value="disposition"><DispositionTab patientId={id} onChange={load} /></TabsContent>
        <TabsContent value="allergies"><AllergiesTab patientId={id} onChange={setAllergies} readOnly={isDispositioned} /></TabsContent>
        <TabsContent value="rpm"><RpmTab patientId={id} summary={summary} /></TabsContent>
        {canCareTasks && <TabsContent value="reminders"><CareTasksTab patientId={id} canManage={canCareTasks && !isDispositioned} /></TabsContent>}
        <TabsContent value="thresholds"><ThresholdsTab patientId={id} readOnly={isDispositioned} /></TabsContent>
        <TabsContent value="teleconsult"><TeleconsultTab patientId={id} readOnly={isDispositioned} /></TabsContent>
        <TabsContent value="hotline"><HotlineTab patientId={id} readOnly={isDispositioned} /></TabsContent>
        <TabsContent value="orders">
          <OrdersTab patientId={id} allergies={allergies} readOnly={isDispositioned} />
        </TabsContent>
        <TabsContent value="mar">
          <MarTab patientId={id} readOnly={isDispositioned} />
        </TabsContent>
        <TabsContent value="notes">
          <NotesTab patientId={id} readOnly={isDispositioned} />
        </TabsContent>
        <TabsContent value="encounters"><EncountersTab patientId={id} /></TabsContent>
        <TabsContent value="otoscope">
          <PatientRecordingsTab patientId={id} device="otoscope"
            canUpload={!isDispositioned} isAdmin={user?.role_code === "admin"} />
        </TabsContent>
        <TabsContent value="stethoscope">
          <PatientRecordingsTab patientId={id} device="stethoscope"
            canUpload={!isDispositioned} isAdmin={user?.role_code === "admin"} />
        </TabsContent>
        <TabsContent value="documents">
          <PatientDocumentsTab patientId={id}
            canUpload={!isDispositioned} isAdmin={user?.role_code === "admin"} />
        </TabsContent>
        <TabsContent value="visits"><HomeVisitsTab patientId={id} /></TabsContent>
        <TabsContent value="services"><ServiceOrdersTab patientId={id} readOnly={isDispositioned} /></TabsContent>
        <TabsContent value="timeline"><TimelineTab patientId={id} /></TabsContent>
        <TabsContent value="audit"><AuditTab patientId={id} /></TabsContent>
        <TabsContent value="episodes">
          <PatientEpisodes mrn={p.mrn} nationalId={p.national_id} patientName={`${p.first_name || ""} ${p.last_name || ""}`.trim()} dischargeDate={p.discharge_date} patientStatus={p.status} />
        </TabsContent>
        {placedForms.length > 0 && (
          <TabsContent value="forms">
            <EmbeddedForms module="patient" entityId={id} entityLabel={p.mrn} forms={placedForms} readOnly={isDispositioned} />
          </TabsContent>
        )}
      </Tabs>

      <ManualVitalsDialog
        patientId={id}
        open={vitalsOpen}
        onOpenChange={setVitalsOpen}
        onSubmitted={() => load()}
      />

      <IotDeviceDialog
        patientId={id}
        open={iotOpen}
        onOpenChange={setIotOpen}
        onSubmitted={() => load()}
      />
    </div>
  );
}
